import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ListboxConfig, ListboxItem, ListboxTableComponent } from '@groundworks/ngx-groundworks-ui';
import { DateTime } from 'luxon';
import { AppointmentTypeDataConstants } from '../../constants/appointment-type-data.constants';
import { AppointmentHistoryItem, mapSalesAppointment } from '../../models/appointment-history-item.model';
import { observeUserRoles$ } from '../../models/roles.model';
import { QuoteRequest } from '../../sdk/sdk';
import { DateService } from '../../services/date.service';
import { ModalService } from '../../services/modal.service';
import { QuoteService } from '../../services/quote.service';

@Component({
  selector: 'app-appointment-history',
  imports: [ListboxTableComponent],
  templateUrl: './appointment-history.component.html',
})
export class AppointmentHistoryComponent {
  readonly appointments = input<AppointmentHistoryItem[] | null>(null);
  readonly isDisabled = input<boolean>(false);
  readonly showRemoveFromFollowUpButton = input<boolean>(false);
  readonly isPendingSale = input<boolean>(true);

  private readonly quoteService = inject(QuoteService);
  private readonly modalService = inject(ModalService);
  private readonly dateService = inject(DateService);

  showSearch = false;
  showPagination = false;

  tableConfig = computed<ListboxConfig>(() => {
    const baseColumns = [
      { header: 'Date/Time', field: 'dateTime', sortable: false },
      { header: 'Type', field: 'type', sortable: false },
      { header: 'CFI/Service Tech/ISR', field: 'cfiSalesRep', sortable: false },
      { header: 'Dispo', field: 'disposition', sortable: false },
      { header: 'SubDispo', field: 'subDisposition', sortable: false },
    ];

    if (this.canEditDisposition()) {
      return {
        columns: [...baseColumns, { header: 'Actions', field: 'action', sortable: false }],
      };
    }

    return { columns: baseColumns };
  });

  tableData = computed<ListboxItem[]>(() => {
    const appointmentsValue = this.appointments();
    const canEdit = this.canEditDisposition();

    return (
      appointmentsValue?.map((appointment) => {
        const item: ListboxItem = {
          id: appointment.id,
          dateTime: this.dateService.formatDateTime(appointment.startDate),
          type: appointment.appointmentTypeName,
          cfiSalesRep: appointment.assigneeName,
          disposition: appointment.dispositionName,
          subDisposition: appointment.subDispositionName,
        };

        if (canEdit) {
          item['action'] = appointment.id;
        }

        return item;
      }) ?? []
    );
  });

  scheduleRevisitClick = output<void>();
  removeFromFollowUpClick = output<void>();
  appointmentCreated = output<AppointmentHistoryItem>();
  dispositionUpdated = output<void>();

  userRoles = toSignal(observeUserRoles$(), { requireSync: true });

  canEditDisposition = computed(() => {
    const roles = this.userRoles();
    const hasPermission = roles?.SalesManager || roles?.SuperUser || roles?.ServiceManager;
    const isOnPendingSalePage = this.isPendingSale();
    const isEnabled = !this.isDisabled();

    return hasPermission && isOnPendingSalePage && isEnabled;
  });

  IsInsideSales = computed(() => {
    const roles = this.userRoles();
    return roles?.InsideSales || roles?.SuperUser || false;
  });

  scheduleButtonLabel = computed(() => {
    const latestNonVirtual = this.appointments()?.find(
      (a) => a.appointmentTypeId !== AppointmentTypeDataConstants.VirtualAppointment
    );
    return latestNonVirtual?.isServiceRun ? 'Schedule Service' : 'Schedule Revisit';
  });

  handleActionClick(appointmentId: string): void {
    if (this.isDisabled()) {
      return;
    }
    const appointment = this.appointments()?.find((apt) => apt.id === appointmentId);
    if (!appointment) {
      console.error('Appointment not found for ID:', appointmentId);
      return;
    }
    this.openEditDispositionModal(appointment);
  }

  private openEditDispositionModal(appointment: AppointmentHistoryItem): void {
    const modalRef = this.modalService.openEditDispositionModal(appointment);

    modalRef.result.then(
      (updated: boolean) => {
        if (updated) {
          this.dispositionUpdated.emit();
        }
      },
      () => {
        // Modal dismissed
      }
    );
  }

  generateQuote(): void {
    const appointmentsValue = this.appointments();
    if (!appointmentsValue || appointmentsValue.length === 0) {
      console.error('Missing appointment data');
      return;
    }

    const previousAppointment = appointmentsValue[0];
    const startDate = DateTime.local();
    const endDate = startDate.plus({ hours: 4 });

    const startDateForServer = new Date(
      Date.UTC(
        startDate.year,
        startDate.month - 1,
        startDate.day,
        startDate.hour,
        startDate.minute,
        startDate.second
      )
    );
    const endDateForServer = new Date(
      Date.UTC(
        endDate.year,
        endDate.month - 1,
        endDate.day,
        endDate.hour,
        endDate.minute,
        endDate.second
      )
    );

    const body = new QuoteRequest({
      opportunityId: previousAppointment.opportunityId,
      startDate: startDateForServer,
      endDate: endDateForServer,
      appointmentTypeId: AppointmentTypeDataConstants.VirtualAppointment,
      leadTypeId: previousAppointment.leadTypeId,
      geoZoneId: previousAppointment.geoZoneId,
      branchId: previousAppointment.branchId,
    });

    this.quoteService.generateQuote(body).subscribe({
      next: (appointmentInfo) => {
        this.appointmentCreated.emit(mapSalesAppointment(appointmentInfo));
        this.modalService.openSuccessModal(
          'Quote Generated',
          'The quote has been successfully generated.'
        );
      },
      error: (error) => {
        console.error('Failed to generate quote:', error);
        let errorMessage: string;

        if (error?.status === 403) {
          errorMessage = 'The current logged in user is not allowed to perform this action';
        } else {
          errorMessage =
            error?.detail ??
            error?.message ??
            'An unexpected error occurred while generating the quote. Please try again.';
        }

        this.modalService.openErrorModal('Error Generating Quote', errorMessage);
      },
    });
  }
}
